import { createSelector } from 'reselect';
import _get from 'lodash/get';

const getVideoModule = state => state.communicationVideo;

export default {
  isCalling: createSelector(getVideoModule, state => state.local && state.local.isCalling),
  getType: createSelector(getVideoModule, state => state.type),
  getLocalCall: createSelector(getVideoModule, state => state.local),
  getRemoteCalls: createSelector(getVideoModule, state => state.remotes),
  getStatus: createSelector(getVideoModule, state => state.status),
  getActiveRemoteCalls: createSelector(getVideoModule, getActiveRemoteCalls),
  isInCall: createSelector(getVideoModule, state => !!getActiveRemoteCalls(state).length),
  getRemoteCall: fromId =>
    createSelector(getVideoModule, state => state.remotes.find(remote => remote.user.id === fromId)),
  getActiveRemoteCall: fromId =>
    createSelector(getVideoModule, state => getActiveRemoteCalls(state).find(remote => remote.user.id === fromId)),
  getRemoteCallByUUID: uuid =>
    createSelector(getVideoModule, state => {
      return state.remotes.find(remote => remote.uuid === uuid);
    }),
  getFirstRemoteCall: createSelector(getVideoModule, state => getActiveRemoteCalls(state)[0]),
  getFirstRemoteCallUserId: createSelector(getVideoModule, state =>
    _get(getActiveRemoteCalls(state), '[0].user.id', null)
  ),
  getFirstRemoteCallUserRole: createSelector(getVideoModule, state =>
    _get(getActiveRemoteCalls(state), '[0].user.role', null)
  ),
  getActiveCallId: createSelector(getVideoModule, state => {
    const activeCall = getActiveRemoteCalls(state)[0];
    return activeCall ? activeCall.uuid : null;
  }),
  getLocalUser: createSelector(getVideoModule, state => (state.local ? state.local.user : null)),
  getLocalStream: createSelector(getVideoModule, state => (state.local ? state.local.stream : null)),
  isRemoteCalling: createSelector(getVideoModule, state => state.remotes.some(({ isCalling }) => isCalling)),
  isCallAccepted: createSelector(
    getVideoModule,
    state => isCallAcceptedByLocal(state) || isCallAcceptedByRemote(state)
  ),
  isCallAcceptedByLocal: createSelector(getVideoModule, isCallAcceptedByLocal),
  isCallAcceptedByRemote: createSelector(getVideoModule, isCallAcceptedByRemote),
  isUserCallAccepted: userId =>
    createSelector(getVideoModule, state => (state.remotes.find(remote => remote.user.id === userId) || {}).isAccepted),
  isIncommingCall: createSelector(getVideoModule, isIncomingCall),
  isOutgoingCall: createSelector(getVideoModule, state =>
    state.remotes.some(({ direction }) => direction === 'outgoing')
  ),
  getCameraFacingMode: createSelector(getVideoModule, state => state.local && state.local.cameraFacingMode),
  isCameraEnabled: createSelector(getVideoModule, state => state.local && state.local.isCameraEnabled),
  isMicMuted: createSelector(getVideoModule, state => state.local && state.local.isMicrophoneMuted),
  isScreanSharingEnabled: createSelector(getVideoModule, state => state.local && state.local.isScreenSharingEnabled),
  hasPendingCalls: createSelector(getVideoModule, state =>
    state.remotes.some(({ isEnded, isRejected, isCancelled }) => !isEnded && !isRejected && !isCancelled)
  ),
  hasStreamWithVideo: createSelector(
    getVideoModule,
    state => state.local && state.local.stream && !!state.local.stream.getVideoTracks().length
  ),
  hasRemoteStream: createSelector(getVideoModule, state => getActiveRemoteCalls(state).some(remote => remote.stream)),
  isChatVisible: createSelector(getVideoModule, state => state.isChatVisible),
  isInit: createSelector(getVideoModule, state => state.isInit),
  getSocketObj: state => {
    const callState = Object.values(state.communicationVideo.remotes)[0];
    const remoteUserId = callState ? callState.user.id : null;
    const remoteUserName = callState ? callState.user.name : null;
    const remoteUserRole = callState ? callState.user.role : null;
    const { isAnonymous } = state.communication;
    const toId = isAnonymous ? state.communication.anonymous.email : state.auth.username;
    const toName = isAnonymous
      ? state.communication.anonymous.name
      : `${state.auth.profile.first_name} ${state.auth.profile.last_name}`;
    const isIncoming = isIncomingCall(state.communicationVideo);

    return isIncoming
      ? { to: toId, toName: toName, from: remoteUserId, fromName: remoteUserName, role: remoteUserRole }
      : { to: remoteUserId, toName: remoteUserName, from: toId, fromName: toName, role: remoteUserRole };
  },
};

function isIncomingCall(state) {
  return state.remotes.some(({ direction }) => direction === 'incoming');
}

function getActiveRemoteCalls(state) {
  return state.remotes.filter(({ isEnded, isRejected, isCancelled }) => !isEnded && !isRejected && !isCancelled);
}

function isCallAcceptedByLocal(state) {
  return state.local && state.local.isAccepted;
}

function isCallAcceptedByRemote(state) {
  return state.remotes.some(state => state.isAccepted);
}
